import { Accordion, AccordionItem, Input, Select, SelectItem, Switch, Textarea } from '@nextui-org/react';
import { Icon } from '@iconify/react';
import { CtyButton } from '@circuitry-ai/doc-ui';
import {
  BUSINESS_TOOL_HTTP_METHODS,
  createEmptyPreRequest,
  type BusinessToolFormState,
  type PreRequestFormRow,
} from './businessToolFormUtils';
import { KeyValueEditor, SectionHeading } from './businessToolFormControls';

export type BusinessToolPreRequestsSectionProps = Readonly<{
  preRequests: BusinessToolFormState['pre_requests'];
  existingCacheKeys: string[];
  onSetPreRequests: (preRequests: BusinessToolFormState['pre_requests']) => void;
  onUpdatePreRequest: (id: string, patch: Partial<PreRequestFormRow>) => void;
}>;

export function BusinessToolPreRequestsSection({
  preRequests,
  existingCacheKeys,
  onSetPreRequests,
  onUpdatePreRequest,
}: BusinessToolPreRequestsSectionProps) {
  return (
    <div className="flex flex-col gap-3">
      <SectionHeading
        title="Pre-requests"
        description="Optional advanced setup calls that run before the main API call."
        action={
          <CtyButton
            size="sm"
            variant="flat"
            startContent={<Icon icon="mdi:plus" width={14} height={14} />}
            onPress={() => onSetPreRequests([...preRequests, createEmptyPreRequest()])}
          >
            Add Pre-request
          </CtyButton>
        }
      />
      {preRequests.length === 0 ? (
        <p className="text-xs text-default-400">Most tools need 0–1 pre-requests.</p>
      ) : (
        <Accordion
          variant="bordered"
          selectionMode="multiple"
          defaultExpandedKeys={preRequests.map((p) => p.id)}
        >
          {preRequests.map((pre, index) => (
            <AccordionItem
              key={pre.id}
              aria-label={pre.name || `Pre-request ${index + 1}`}
              title={
                <span className="text-sm font-medium">
                  {pre.name.trim() || `Pre-request ${index + 1}`}
                </span>
              }
              startContent={
                <CtyButton
                  isIconOnly
                  size="sm"
                  variant="light"
                  color="danger"
                  aria-label="Remove pre-request"
                  onPress={() => onSetPreRequests(preRequests.filter((item) => item.id !== pre.id))}
                >
                  <Icon icon="mdi:close" width={16} height={16} />
                </CtyButton>
              }
            >
              <div className="flex flex-col gap-3 pb-2">
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <Input
                    size="sm"
                    label="Name"
                    labelPlacement="outside"
                    isRequired
                    variant="bordered"
                    value={pre.name}
                    onValueChange={(value) => onUpdatePreRequest(pre.id, { name: value })}
                  />
                  <Input
                    size="sm"
                    label="Description"
                    labelPlacement="outside"
                    isRequired
                    variant="bordered"
                    value={pre.description}
                    onValueChange={(value) => onUpdatePreRequest(pre.id, { description: value })}
                  />
                  <Input
                    size="sm"
                    label="Endpoint"
                    labelPlacement="outside"
                    isRequired
                    variant="bordered"
                    placeholder="/start-stream"
                    value={pre.endpoint}
                    onValueChange={(value) => onUpdatePreRequest(pre.id, { endpoint: value })}
                    description="Relative to the main API's Base URL below."
                  />
                  <div className="grid grid-cols-2 gap-3">
                    <Select
                      size="sm"
                      label="Method"
                      labelPlacement="outside"
                      variant="bordered"
                      selectedKeys={[pre.method]}
                      onSelectionChange={(keys) => {
                        const value = Array.from(keys)[0] as PreRequestFormRow['method'] | undefined;
                        if (value) onUpdatePreRequest(pre.id, { method: value });
                      }}
                    >
                      {BUSINESS_TOOL_HTTP_METHODS.map((method) => (
                        <SelectItem key={method}>{method}</SelectItem>
                      ))}
                    </Select>
                    <Input
                      size="sm"
                      type="number"
                      label="Timeout (seconds)"
                      labelPlacement="outside"
                      variant="bordered"
                      min={1}
                      max={60}
                      value={String(pre.timeout_seconds)}
                      onValueChange={(value) =>
                        onUpdatePreRequest(pre.id, {
                          timeout_seconds: Math.min(60, Math.max(1, Number(value) || 1)),
                        })
                      }
                    />
                  </div>
                </div>
                <div>
                  <p className="text-xs text-default-500 mb-2">Headers</p>
                  <KeyValueEditor
                    pairs={pre.headers}
                    onChange={(headers) => onUpdatePreRequest(pre.id, { headers })}
                    keyLabel="Header"
                    valueLabel="Value"
                    addLabel="Add header"
                  />
                </div>
                <Textarea
                  size="sm"
                  label="Body Template (JSON)"
                  labelPlacement="outside"
                  variant="bordered"
                  minRows={4}
                  value={pre.body_template}
                  onValueChange={(value) => onUpdatePreRequest(pre.id, { body_template: value })}
                  placeholder={`{\n  "payload": { "aiworker_id": "{{aiworker_id}}" }\n}`}
                  classNames={{ input: 'font-mono text-sm' }}
                  description="Supports {{param}} placeholders"
                />
                <div>
                  <p className="text-xs text-default-500 mb-2">Query params</p>
                  <KeyValueEditor
                    pairs={pre.query_params}
                    onChange={(query_params) => onUpdatePreRequest(pre.id, { query_params })}
                    keyLabel="Key"
                    valueLabel="Value"
                    addLabel="Add query param"
                  />
                </div>
                <div>
                  <p className="text-xs font-medium text-default-600 mb-1">Extract fields *</p>
                  <p className="text-xs text-default-400 mb-2">
                    Variable name → JSON path in response. Becomes {'{{pre_request.<variable>}}'}.
                  </p>
                  <KeyValueEditor
                    pairs={pre.extract_fields}
                    onChange={(extract_fields) => onUpdatePreRequest(pre.id, { extract_fields })}
                    keyLabel="Variable name"
                    valueLabel="JSON path"
                    addLabel="Add extract field"
                  />
                </div>
                <div className="flex flex-col gap-2 rounded-lg border border-divider p-3">
                  <Switch
                    size="sm"
                    isSelected={pre.cache_enabled}
                    onValueChange={(value) => onUpdatePreRequest(pre.id, { cache_enabled: value })}
                  >
                    Enable cache
                  </Switch>
                  {pre.cache_enabled ? (
                    <>
                      <Input
                        size="sm"
                        label="Cache key"
                        labelPlacement="outside"
                        variant="bordered"
                        value={pre.cache_key}
                        onValueChange={(value) => onUpdatePreRequest(pre.id, { cache_key: value })}
                        description="Tools with the SAME cache key share one cached result/session for the whole call. Use the same key across tools that must run in the same workflow session."
                      />
                      {existingCacheKeys.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          <span className="text-xs text-default-400 w-full">Existing cache keys:</span>
                          {existingCacheKeys.map((key) => (
                            <button
                              key={key}
                              type="button"
                              className="inline-flex items-center rounded-full border border-divider bg-default-100 px-2 py-0.5 font-mono text-[11px] text-default-700 hover:border-primary"
                              onClick={() => onUpdatePreRequest(pre.id, { cache_key: key })}
                            >
                              {key}
                            </button>
                          ))}
                        </div>
                      ) : null}
                    </>
                  ) : null}
                </div>
              </div>
            </AccordionItem>
          ))}
        </Accordion>
      )}
    </div>
  );
}
